import type React from "react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { Form } from "@/components/Form/Form";
import { FormTagSelect } from "@/components/Form/FormTagSelect";
import { FormTextArea } from "@/components/Form/FormTextArea";
import { MessageSmileSquareIcon } from "@/components/Icons/MessageSmileSquareIcon";
import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Notice } from "@/design-system/Notice";
import { askNobiChatMutations } from "@/queries/ask-nobi/mutations";
import { useTranslation } from "@/translations";

type IssueKey =
  | "incorrect"
  | "irrelevant"
  | "unclear"
  | "too-long"
  | "safety-issue"
  | "tool-data-issue"
  | "other"
  | "incomplete"
  | "too-short";
const ISSUE_KEYS = [
  "incorrect",
  "incomplete",
  "irrelevant",
  "unclear",
  "too-long",
  "too-short",
  "safety-issue",
  "tool-data-issue",
  "other",
] as const satisfies IssueKey[];

type ImpactKey = "minor" | "major" | "blocking";
const IMPACT_KEYS = ["minor", "major", "blocking"] as const satisfies ImpactKey[];

interface FormValues {
  issues: IssueKey[];
  impact: ImpactKey[];
  comment: string;
}

const FORM_ID = "negative-feedback-modal-form";

interface AskNobiNegativeFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
  /** When absent, rates at the conversation level (enrichment mode). */
  messageId?: string;
  onRated: () => void;
}

export function AskNobiNegativeFeedbackModal({
  isOpen,
  onClose,
  conversationId,
  messageId,
  onRated,
}: AskNobiNegativeFeedbackModalProps): React.ReactNode {
  const { t } = useTranslation();
  const isConversationMode = !messageId;
  const form = useForm<FormValues>({ defaultValues: { issues: [], impact: [], comment: "" } });
  const rateMessage = askNobiChatMutations.useRateMessage();
  const rateConversation = askNobiChatMutations.useRateConversation();
  const isPending = rateConversation.isPending || rateMessage.isPending;

  useEffect(() => {
    form.reset();
  }, [form, isOpen]);

  async function handleSubmit(values: FormValues) {
    const payload = {
      issues: values.issues,
      ...(values.impact.length > 0 && { impact: values.impact[0] }),
      ...(values.comment.trim() && { comment: values.comment.trim() }),
    };
    const comment = JSON.stringify(payload);

    if (isConversationMode) {
      await rateConversation.mutateAsync({
        conversationId,
        data: { rating: "negative", comment },
      });
    } else {
      await rateMessage.mutateAsync({
        conversationId,
        messageId: messageId,
        data: { rating: "negative", comment },
      });
    }
    onRated();
    onClose();
  }

  function handleSkip() {
    // The negative rating was already recorded on thumbs-down click for both message and conversation mode
    onRated();
    onClose();
  }

  const issueItems = ISSUE_KEYS.map((key) => ({
    value: key,
    label: t(`component.ask-nobi-chat.feedback-modal.issues.${key}`),
  }));

  const impactItems = IMPACT_KEYS.map((key) => ({
    value: key,
    label: t(`component.ask-nobi-chat.feedback-modal.impact.${key}`),
  }));

  return (
    <Modal.Root
      isOpened={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title={t("component.ask-nobi-chat.feedback-modal.title")}
      isNested
    >
      <Modal.Body>
        <Form.Root id={FORM_ID} formMethods={form} onSubmit={handleSubmit} noPrompt>
          <Form.Body>
            <FormField label={t("component.ask-nobi-chat.feedback-modal.issues.label")} required>
              <FormTagSelect<FormValues>
                name="issues"
                items={issueItems}
                isMultiple
                rules={{
                  validate: (v) =>
                    v.length > 0 ||
                    t("components.form.error.required", {
                      inputName: t("component.ask-nobi-chat.feedback-modal.issues.label"),
                    }),
                }}
                data-testid="negative-feedback-modal-issues"
              />
            </FormField>

            {isConversationMode && (
              <FormField label={t("component.ask-nobi-chat.feedback-modal.impact.label")}>
                <FormTagSelect<FormValues>
                  name="impact"
                  items={impactItems}
                  data-testid="negative-feedback-modal-impact"
                />
              </FormField>
            )}

            <FormField label={t("component.ask-nobi-chat.feedback-modal.comment.label")}>
              <FormTextArea<FormValues>
                name="comment"
                placeholder={t("component.ask-nobi-chat.feedback-modal.comment.placeholder")}
                data-testid="negative-feedback-modal-comment"
              />
            </FormField>

            <Notice
              icon={<MessageSmileSquareIcon />}
              variant="special"
              message={t("component.ask-nobi-chat.feedback-modal.notice")}
            />
          </Form.Body>
        </Form.Root>
      </Modal.Body>

      <Modal.Actions>
        <Button
          variant="outline"
          onClick={handleSkip}
          disabled={isPending}
          data-testid="negative-feedback-modal-skip-btn"
        >
          {t("component.ask-nobi-chat.feedback-modal.action.skip")}
        </Button>
        <Button form={FORM_ID} type="submit" isLoading={isPending} data-testid="negative-feedback-modal-submit-btn">
          {t("component.ask-nobi-chat.feedback-modal.action.submit")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
